"use client";

import styles from "@/styles/modules/components/header/bookMenuOptions/bookMenuOptions.module.scss";

import { BookMenuContext } from "@/context/bookMenuContext";
import { LanguageContext } from "@/context/languageContext";

import { translate } from "@/utils/translate";
import { formatDate, formatDateMarriott } from "@/utils/formatDate";
import { usePathname } from "next/navigation";
import { FormEvent, useContext, useEffect, useRef, useState } from "react";

export default function BookMenuBedrooms() {
  const pathname = usePathname();

  const { language } = useContext(LanguageContext);

  const arrivalRef = useRef<HTMLInputElement>(null);
  const departureRef = useRef<HTMLInputElement>(null);

  const [arrivalValue, setArrivalValue] = useState<string>("");
  const [departureValue, setDepartureValue] = useState<string>("");
  const [roomsValue, setRoomsValue] = useState<string>("1");
  const [guestsValue, setGuestsValue] = useState<string>("1");

  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | ''>('');

  const { closeBookMenu } = useContext(BookMenuContext); // Access closeBookMenu from BookMenuContext

  const setupValuesCheckInCheckOut = () => {
    const today = new Date();
    const nextDay = new Date(today);
    const nextYear = new Date(today);

    nextYear.setFullYear(today.getFullYear() + 1);
    nextDay.setDate(nextDay.getDate() + 1); // Set checkout date to the following day

    if (departureRef.current) {
      departureRef.current.min = today.toISOString().substring(0, 10);
      departureRef.current.max = nextYear.toISOString().substring(0, 10);
      departureRef.current.value = nextDay.toISOString().substring(0, 10);
    }
    if (arrivalRef.current) {
      arrivalRef.current.value = today.toISOString().substring(0, 10);
    }

  }
  const handleMenuClose = () => {
    // Clear form fields
    setupValuesCheckInCheckOut();
    setRoomsValue("1");
    setGuestsValue("1");
    // Reset submitStatus
    setSubmitStatus('');
  };

  useEffect(() => {
    setupValuesCheckInCheckOut();
    setRoomsValue("1");
    setGuestsValue("1");
  }, [pathname]);

  useEffect(() => {
    handleMenuClose();
  }, [submitStatus]);

  useEffect(() => {

    setupValuesCheckInCheckOut();
    // Add click event listener to open calendar when clicking anywhere on the input fields
    const handleArrivalClick = () => {
      if (arrivalRef.current) {
        arrivalRef.current.showPicker();
      }
    };
    const handleDepartureClick = () => {
      if (departureRef.current) {
        departureRef.current.showPicker();
      }
    };

    if (arrivalRef.current) {
      arrivalRef.current.addEventListener('click', handleArrivalClick);
    }
    if (departureRef.current) {
      departureRef.current.addEventListener('click', handleDepartureClick);
    }

    return () => {
      if (arrivalRef.current) {
        arrivalRef.current.removeEventListener('click', handleArrivalClick);
      }
      if (departureRef.current) {
        departureRef.current.removeEventListener('click', handleDepartureClick);
      }
    };
  }, []);

  useEffect(() => {
    if (departureRef.current) {
      setDepartureValue(departureRef.current?.value);
    }
  }, [departureRef.current?.value]);

  const handleCheckinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checkin = new Date(e.target.value);
    const nextDay = new Date(checkin);
    const checkoutMin = new Date(checkin);
    const checkoutMax = new Date(checkin);

    checkoutMax.setFullYear(checkin.getFullYear() + 1);
    nextDay.setDate(nextDay.getDate() + 1); // Set checkout date to the following day

    if (departureRef.current) {
      departureRef.current.min = formatDate(checkoutMin);
      departureRef.current.max = formatDate(checkoutMax);
      departureRef.current.value = formatDate(nextDay);
      setDepartureValue(departureRef.current?.value);
    }

    setArrivalValue(e.target.value)
  };

  const handleCheckoutChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (departureRef.current) {
      setDepartureValue(departureRef.current?.value);
    }
  };


  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const url = `https://www.marriott.com/${language === 'de' ? 'de/' : ''}`;
    window.open(
      `${url}reservation/availabilitySearch.mi?propertyCode=VIEIR&fromDate=${formatDateMarriott(
        arrivalRef.current!.value
      )}&toDate=${formatDateMarriott(
        departureRef.current!.value
      )}&roomCount=${roomsValue}&numberOfGuests=${guestsValue}`,
      "_blank"
    );

    setTimeout(() => {
      setSubmitStatus('success');
      closeBookMenu(); // Call closeBookMenu from BookMenuContext on successful form submission
    }, 2000);
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <div className={styles.formField}>
        <label htmlFor="arrival">{translate["arrival"][language]}</label>
        <input
          type="date"
          name="arrival"
          id="arrival"
          ref={arrivalRef}
          onChange={handleCheckinChange}
          min={new Date().toISOString().substring(0, 10)}
          max={new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().substring(0, 10)}
          required
        />
      </div>
      <div className={styles.formField}>
        <label htmlFor="departure">{translate["departure"][language]}</label>
        <input
          type="date"
          name="departure"
          id="departure"
          ref={departureRef}
          value={departureValue}
          onChange={handleCheckoutChange}
          required
        />
      </div>
      <div className={styles.formRow}>
        <div className={`${styles.formField} ${styles.oneOfThree}`}>
          <label htmlFor="rooms">{translate["rooms"][language]}</label>
          <select
            name="rooms"
            id="rooms"
            value={roomsValue}
            onChange={(e) => setRoomsValue(e.target.value)}
            required
          >
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
          </select>
        </div>
        <div className={`${styles.formField} ${styles.twoOfThree}`}>
          <label htmlFor="guests">{translate["guests"][language]}</label>
          <select
            name="guests"
            id="guests"
            value={guestsValue}
            onChange={(e) => setGuestsValue(e.target.value)}
            required
          >
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
            <option value="5">5</option>
            <option value="6">6</option>
            <option value="7">7</option>
            <option value="8">8</option>
          </select>
        </div>
      </div>
      <div className={styles.formField}>
        <input
          type="submit"
          name="submit"
          value={translate["bookNow"][language]}
        />
      </div>
    </form>
  );
}


